import { Icon } from '@/new/components/icon';
import { NewChip } from '@/new/components/new-chip';

interface GetStartedTemplatesProps {
  onSelectTemplate: (templateId: string, customPrompt?: string) => void;
}

export function GetStartedTemplates({ onSelectTemplate }: GetStartedTemplatesProps) {
  return (
    <div>
      <h2 className="mb-1 font-medium">Get started in this Matter</h2>
      <div className="-mx-1.25 mt-3.5 flex items-start gap-4">
        <button
          type="button"
          onClick={() => onSelectTemplate('chronology')}
          className="group basis-1/3 cursor-pointer text-left"
        >
          <div className="relative h-22 w-full overflow-hidden rounded-[10px] border border-gray-300/80 bg-gray-50 duration-150 group-hover:border-gray-400/60">
            <div className="absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-1.5 duration-150 group-hover:-translate-y-[calc(50%+2px)]">
              <div className="space-y-0.75 rounded-lg border border-gray-300/80 bg-white p-1.25 shadow-lg/4 duration-150 group-hover:border-gray-300 group-hover:shadow-lg/5">
                <div className="h-2 w-9 rounded-sm bg-gray-200"></div>
                <div className="h-2 w-28 rounded-sm bg-gray-100/80"></div>
              </div>
              <div className="space-y-0.75 rounded-lg border border-gray-300/80 bg-white p-1.25 shadow-lg/4 duration-150 group-hover:border-gray-300 group-hover:shadow-lg/5">
                <div className="h-2 w-8 rounded-sm bg-gray-200"></div>
                <div className="h-2 w-28 rounded-sm bg-gray-100/80"></div>
              </div>
              <div className="space-y-0.75 rounded-lg border border-gray-300/80 bg-white p-1.25 shadow-lg/4 duration-150 group-hover:border-gray-300 group-hover:shadow-lg/5">
                <div className="h-2 w-10 rounded-sm bg-gray-200"></div>
                <div className="h-2 w-28 rounded-sm bg-gray-100/80"></div>
              </div>
            </div>
          </div>
          <div className="space-y-0.75 px-1.5 pt-2.5 pb-1 text-[13px]">
            <p className="font-medium">Build a chronology with Kim</p>
            <p className="text-xs text-gray-500">
              Refine your search to focus on key issues, entities, or date ranges.
            </p>
          </div>
        </button>
        <button
          type="button"
          onClick={() => onSelectTemplate('inconsistencies')}
          className="group basis-1/3 cursor-pointer text-left"
        >
          <div className="relative flex h-22 w-full items-center justify-center overflow-hidden rounded-[10px] border border-gray-300/80 bg-gray-50 duration-150 group-hover:border-gray-400/60">
            <div className="relative mt-0.5 flex items-center gap-3 duration-150">
              <div className="h-25 w-19 -rotate-2 space-y-1 overflow-hidden rounded-lg border border-gray-300/80 bg-white p-2 shadow-lg/4 duration-150 group-hover:border-gray-300 group-hover:shadow-lg/5">
                <div className="mb-1.5 h-1.5 w-10 rounded-xs bg-gray-300"></div>
                <div className="h-1.25 w-full rounded-sm bg-gray-100"></div>
                <div className="h-1.25 w-4/5 rounded-sm bg-gray-100"></div>
                <div className="h-1.25 w-8 rounded-sm bg-gray-200"></div>
                <div className="h-1.25 w-full rounded-sm bg-gray-100"></div>
                <div className="h-1.25 w-5/6 rounded-sm bg-gray-100"></div>
                <div className="h-1.25 w-full rounded-sm bg-gray-100"></div>
                <div className="h-1.25 w-3/5 rounded-sm bg-gray-100"></div>
                <div className="h-1.25 w-full rounded-sm bg-gray-100"></div>
              </div>
              <div className="h-25 w-19 rotate-2 space-y-1 overflow-hidden rounded-lg border border-gray-300/80 bg-white p-2 shadow-lg/4 duration-150 group-hover:border-gray-300 group-hover:shadow-lg/5">
                <div className="mb-1.5 h-1.5 w-9 rounded-xs bg-gray-300"></div>
                <div className="h-1.25 w-5/6 rounded-sm bg-gray-100"></div>
                <div className="h-1.25 w-full rounded-sm bg-gray-100"></div>
                <div className="h-1.25 w-4/5 rounded-sm bg-gray-100"></div>
                <div className="h-1.25 w-full rounded-sm bg-gray-100"></div>
                <div className="h-1.25 w-6 rounded-sm bg-gray-200"></div>
                <div className="h-1.25 w-3/4 rounded-sm bg-gray-100"></div>
                <div className="h-1.25 w-full rounded-sm bg-gray-100"></div>
                <div className="h-1.25 w-2/3 rounded-sm bg-gray-100"></div>
              </div>
              <div className="absolute top-1/2 left-1/2 flex h-6.5 w-6.5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-red-200 bg-white text-red-500 shadow-lg/5 duration-150 group-hover:scale-105">
                <Icon className="lucide--shield-alert text-base" />
              </div>
            </div>

            <NewChip className="top-auto right-auto bottom-1.75 left-1.75" />
          </div>
          <div className="px-1.2 space-y-1 pt-2.5 pb-1 text-[13px]">
            <p className="font-medium">Surface inconsistencies</p>
            <p className="text-xs text-gray-500">Attach ad-hoc files in Kim to compare against existing evidence.</p>
          </div>
        </button>
        <button
          type="button"
          onClick={() =>
            onSelectTemplate(
              'document-draft',
              'Draft an early case assessment for this matter. Surface key issues and any potential gaps.',
            )
          }
          className="group basis-1/3 cursor-pointer text-left"
        >
          <div className="relative flex h-22 w-full items-end justify-center overflow-hidden rounded-[10px] border border-gray-300/80 bg-gray-50 duration-150 group-hover:border-gray-400/60">
            <div className="w-26 translate-y-10 space-y-1 rounded-t-md border border-b-0 border-gray-300/80 bg-white px-3 py-3 shadow-lg/4 duration-150 group-hover:translate-y-9.5 group-hover:border-gray-300 group-hover:shadow-lg/5">
              <div className="mb-2 h-1.5 w-14 rounded-xs bg-gray-300"></div>
              <div className="h-1.25 w-full rounded-sm bg-gray-100"></div>
              <div className="h-1.25 w-4/5 rounded-sm bg-gray-100"></div>
              <div className="h-1.25 w-9 rounded-sm bg-gray-200"></div>
              <div className="h-1.25 w-full rounded-sm bg-gray-100"></div>
              <div className="h-1.25 w-5/6 rounded-sm bg-gray-100"></div>
              <div className="h-1.25 w-7 rounded-sm bg-gray-200"></div>
              <div className="h-1.25 w-full rounded-sm bg-gray-100"></div>
              <div className="h-1.25 w-4/5 rounded-sm bg-gray-100"></div>
            </div>
          </div>
          <div className="space-y-1 px-1.5 pt-2.5 pb-1 text-[13px]">
            <p className="font-medium">Draft an early case assessment</p>
            <p className="text-xs text-gray-500">Get an overview of strengths, weaknesses and gaps.</p>
          </div>
        </button>
      </div>
    </div>
  );
}
